/**
 * Выборка «Забираю» — какие байки скаут сейчас собирает.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ПОЧЕМУ ВНЕШНЕЕ ХРАНИЛИЩЕ, А НЕ `useState` НА ЭКРАНЕ
 * ─────────────────────────────────────────────────────────────────────────
 * До этого выборка жила локальным состоянием экрана «Список» — и умирала
 * при уходе на карточку байка и обратно. Читателей теперь трое (список,
 * карта, карточка), и все трое обязаны видеть одну и ту же отметку: скаут
 * отмечает байк там, где его нашёл, а маршрут строит там, где ему удобно.
 *
 * `useSyncExternalStore`, а не контекст: состояние примитивное (множество
 * строк), меняется по нажатию, и провайдер поверх всего приложения ради
 * него был бы дороже самого состояния.
 *
 * ⚠ localStorage, А НЕ IndexedDB/outbox, И ЭТО НЕ НАРУШЕНИЕ ПРАВИЛА.
 * Здесь не данные API и не бизнес-состояние: сервер про эту выборку не
 * знает вовсе и знать не должен — это заметка скаута себе, ровно как
 * «Недавние» (`lib/recent-bikes.ts`). Переживать перезапуск она обязана:
 * телефон в поле засыпает и убивает вкладку посреди объезда.
 */

import { useSyncExternalStore } from 'react'

export const PICKUP_KEY = 'scout.pickup'

/**
 * Статусы, означающие «байк уже у скаута в руках».
 *
 * Зеркало не машины состояний, а здравого смысла: в маршруте по точкам
 * забранный байк — это адрес, по которому больше некого забирать.
 * `faulty`/`lost` сюда НЕ входят — их как раз и везут. `in_pool` — за ним
 * уже едет водитель (`lib/pool.ts`): выбирать его снова не для чего.
 * `at_base` тоже не входит: заряженный на базе водитель выбирает, чтобы
 * отвезти в хаб, а довезённый скаутом до базы ушёл из выборки ещё на
 * `in_transit`.
 */
const PICKED_UP_STATUSES = new Set(['in_transit', 'in_pool'])

type HasStatus = { id: string; status: string }

let selected: ReadonlySet<string> = read()
const listeners = new Set<() => void>()

function read(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(PICKUP_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

function write(next: ReadonlySet<string>): void {
  try {
    if (next.size === 0) localStorage.removeItem(PICKUP_KEY)
    else localStorage.setItem(PICKUP_KEY, JSON.stringify([...next]))
  } catch {
    /* приватный режим/квота — выбор доживёт до перезапуска и не дальше */
  }
}

function same(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

/**
 * Единственная точка записи.
 *
 * ⚠ ХОЛОСТОЕ ИЗМЕНЕНИЕ ГАСИТСЯ ЗДЕСЬ. `pruneByStatus` зовётся на каждом
 * рефетче парка (раз в 20 с, пока открыта карта); без этой проверки каждый
 * такой тик писал бы в localStorage и перерисовывал карту целиком.
 */
function commit(next: ReadonlySet<string>): void {
  if (same(selected, next)) return
  selected = next
  write(next)
  for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Текущая выборка. Новый объект на каждое изменение — React сравнивает по ссылке. */
export function snapshot(): ReadonlySet<string> {
  return selected
}

export function has(id: string): boolean {
  return selected.has(id)
}

/**
 * Выборка в React-компоненте.
 *
 * Третий аргумент (серверный снимок) — тот же: SSR в приложении нет, но без
 * него `useSyncExternalStore` бросает на первом же рендере в гидратации,
 * если она когда-нибудь появится.
 */
export function usePickupSelection(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

export function toggle(id: string): void {
  const next = new Set(selected)
  if (!next.delete(id)) next.add(id)
  commit(next)
}

/** «Взять все на экране»: только добавляет — повторное нажатие ничего не снимает. */
export function addAll(ids: Iterable<string>): void {
  commit(new Set([...selected, ...ids]))
}

export function remove(id: string): void {
  if (!selected.has(id)) return
  const next = new Set(selected)
  next.delete(id)
  commit(next)
}

export function clear(): void {
  commit(new Set())
}

/**
 * Снять отметку с тех, кого уже забрали.
 *
 * Байк, которого в ответе нет вовсе, НЕ трогается: пустой или частичный
 * ответ (офлайн, ошибка) — это незнание, а не новость «всё забрали», и
 * вычищать по нему работу скаута нельзя.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ `unconfirmed` — НЕ ФИЛЬТР УДОБСТВА, А ЗАЩИТА ОТ ПОТЕРИ РАБОТЫ
 * ─────────────────────────────────────────────────────────────────────────
 * Смена статуса рисуется оптимистично (`api/vehicles.ts`): «В машину»
 * ставит `in_transit` в кэш ДО того, как запись уйдёт с устройства. Если
 * чистить по такому статусу, а отправка потом провалится (приватный режим,
 * кончилась квота), откат вернёт статус — но не отметку: чистка умеет
 * только удалять, и обратного хода у неё нет. Скаут молча теряет байк из
 * своего маршрута.
 *
 * Поэтому сюда передаётся множество id, у которых смена статуса ещё лежит
 * в очереди (`queuedTargets(queue, 'vehicle_status')`). Пока запись там —
 * статус считается нашим предположением, а не фактом, и отметка держится.
 * Ушла запись — значит, сервер согласился, и чистить можно.
 *
 * ⚠ `null` — «очередь ещё не прочитана», и это НЕ то же самое, что пустое
 * множество. Очередь живёт в IndexedDB и доезжает до экрана на кадр позже
 * парка, который приходит синхронно из кэша. На первом кадре каждого
 * монтирования карты пустое множество означало бы «неотправленных нет» —
 * и отметка снималась бы ровно в том случае, ради которого всё это
 * написано. Не знаем — не трогаем.
 *
 * Параметр обязателен намеренно: значение по умолчанию здесь — это
 * молчаливое «неотправленных нет» у любого забывчивого вызывающего.
 */
export function pruneByStatus(
  vehicles: readonly HasStatus[],
  unconfirmed: ReadonlySet<string> | null,
): void {
  if (unconfirmed === null) return
  if (selected.size === 0) return
  const pickedUp = new Set(
    vehicles
      .filter((v) => PICKED_UP_STATUSES.has(v.status) && !unconfirmed.has(v.id))
      .map((v) => v.id),
  )
  if (pickedUp.size === 0) return
  commit(new Set([...selected].filter((id) => !pickedUp.has(id))))
}
